import React, { useCallback, useEffect } from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { societyDocumentFolderCreateSchema } from '../../../../forms/schemas/society_document_folder_create'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { CreateUpdateFolderForm } from '../CreateUpdateFolderForm'
import { createDocument, patchDocument } from '../../../../api/documents'
import { NDocuments } from '../../../../interfaces/services/documents.interfaces'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import { FormatTextPopover } from '../../../feed/CreateEditPostModal/CreateEditPostModalContent/FormatTextPopover'
import { Button, ButtonVariant } from '../../../common/Button'
import { ModalFooter } from '../../../common/Modal/ModalFooter'

interface CreateUpdateFolderModalProps {
  show: boolean
  close: () => void
  section?: IDocumentModel['section']
  parentId: string
  children?: React.ReactNode
  folder?: IDocumentModel
}

export const CreateUpdateFolderModal = ({
  folder,
  parentId,
  section,
  children,
  show,
  close,
}: CreateUpdateFolderModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!folder

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateFolder.flashMessage.updateFolderFailure'
      : 'createUpdateFolder.flashMessage.createFolderFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateFolder.flashMessage.updateFolderSuccess'
      : 'createUpdateFolder.flashMessage.createFolderSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutatePatchDocument, isPending: isPatchingDocument } =
    useMutation({
      mutationFn: async (params: {
        id: string
        body: NDocuments.NPatch.IRequestBody
      }) => {
        return patchDocument({ id: params.id, body: params.body })
      },
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const { mutate: mutateCreateFolder, isPending: isCreatingFolder } =
    useMutation({
      mutationFn: async (params: {
        name: string
        description?: string
        thumbnail?: string
        parentId?: string
        societyId?: string
        section?: IDocumentModel['section']
        notificationSettings?: IDocumentModel['notificationSettings']
      }) => {
        const body: NDocuments.NCreate.IRequestBody = {
          ...{
            name: params.name,
            description: params.description,
            thumbnail: params.thumbnail,
            filename: params.name,
            type: 'folder',
          },
          ...(params.parentId !== undefined && { parentId: params.parentId }),
          ...(params.societyId !== undefined && {
            societyId: params.societyId,
          }),
          ...(params.section !== undefined && { section: params.section }),
          ...(params.notificationSettings !== undefined && {
            notificationSettings: params.notificationSettings,
          }),
        }
        return createDocument(body)
      },
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const getDefaultValues = useCallback((): InferType<
    typeof societyDocumentFolderCreateSchema
  > => {
    return {
      name: updateMode ? folder?.name || '' : '',
      description: updateMode ? folder?.description || '' : '',
      thumbnail: updateMode ? folder?.thumbnail || '' : '',
    }
  }, [updateMode, folder])

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyDocumentFolderCreateSchema),
    defaultValues: getDefaultValues(),
  })

  const onSubmit = async (
    data: InferType<typeof societyDocumentFolderCreateSchema>
  ): Promise<void> => {
    if (folder) {
      mutatePatchDocument({ id: folder._id, body: data })
    } else {
      mutateCreateFolder({
        name: data.name,
        description: data.description,
        thumbnail: data.thumbnail,
        parentId,
        societyId: society?._id,
        notificationSettings: 'none',
        section,
      })
      reset(getDefaultValues())
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const handleClose = (): void => {
    reset(getDefaultValues())
    close()
  }

  useEffect(() => {
    reset(getDefaultValues())
  }, [folder, reset, getDefaultValues])

  const loading = isCreatingFolder || isPatchingDocument

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={handleClose}>
              {translate(
                updateMode
                  ? 'createUpdateFolder.editFolderTitle'
                  : 'createUpdateFolder.addNewFolderTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateFolderForm
                errors={errors}
                setValue={setValue}
                control={control}
              />
            </ModalBody>
          ),
          footer: (
            <ModalFooter className="flex flex-row gap-4 px-6 pb-6">
              <FormatTextPopover />
              <Button
                label={translate('common.actions.cancel')}
                onClick={handleClose}
              />
              <Button
                loading={loading}
                variant={ButtonVariant.PRIMARY}
                disabled={!isValid || loading}
                label={translate(
                  updateMode ? 'common.actions.save' : 'common.actions.create'
                )}
                type="submit"
                onClick={handleSubmit(onSubmit, onError)}
              />
            </ModalFooter>
          ),
        }}
      </Modal>
    </>
  )
}
